/**
 * Centralized mention types and utilities.
 *
 * This module provides a unified type system for handling mentions across
 * the application, including API formats, UI representations, and type guards.
 */

import { agentDisplayName } from "@app/types/assistant/agent";
import { assertNever } from "@app/types/shared/utils/assert_never";
import type { UserType } from "@app/types/user";

/**
 * Base mention structure used in API requests and database storage.
 * This is the minimal representation of an agent mention.
 */
export type AgentMention = {
  // TODO(mentions v2) add a type here
  configurationId: string;
};

/**
 * User mention type for future support of mentioning users in conversations.
 */
export type UserMention = {
  type: "user";
  userId: string;
};

/**
 * Union type of all supported mention types.
 *
 * @swaggerschema Mention (swagger_schemas.ts), PrivateMention (swagger_private_schemas.ts)
 */
export type MentionType = AgentMention | UserMention;

/**
 * Rich mention type with full display metadata.
 * Used in UI components and the editor where we need display information.
 *
 * @swaggerschema RichMention (swagger_schemas.ts), PrivateMentionSuggestion (swagger_private_schemas.ts)
 */
export type RichMention = RichAgentMention | RichUserMention;

interface BaseRichMention {
  id: string; // configurationId for agents, userId for users
  type: "agent" | "user";
  label: string; // Display name
  pictureUrl: string;
  description: string;
}

/**
 * Agent-specific rich mention with additional metadata.
 */
export interface RichAgentMention extends BaseRichMention {
  type: "agent";
  userFavorite?: boolean;
  // A local agent whose computer is offline right now. It stays mentionable — the work waits
  // for the computer to come back — but the picker says so before anyone picks it.
  isOffline?: boolean;
}

export interface RichAgentMentionInConversation extends RichAgentMention {
  isParticipant?: boolean;
  lastActivityAt?: number;
}

/**
 * User-specific rich mention.
 */
export interface RichUserMention extends BaseRichMention {
  type: "user";
}

export interface RichUserMentionInConversation extends RichUserMention {
  isParticipant?: boolean;
  isProjectMember?: boolean;
  lastActivityAt?: number;
}

/**
 * Type guard to check if a mention is an agent mention.
 */
export function isAgentMention(mention: MentionType): mention is AgentMention {
  return (mention as AgentMention).configurationId !== undefined;
}

/**
 * Type guard to check if a rich mention is an agent mention.
 */
export function isRichAgentMention(
  mention: RichMention
): mention is RichAgentMention {
  return mention.type === "agent";
}

/**
 * Type guard to check if a mention is a user mention.
 */
export function isUserMention(mention: MentionType): mention is UserMention {
  return (
    mention &&
    "type" in mention &&
    mention.type === "user" &&
    "userId" in mention &&
    mention.userId !== undefined
  );
}

/**
 * Type guard to check if a rich mention is a user mention.
 */
export function isRichUserMention(
  mention: RichMention
): mention is RichUserMention {
  return mention.type === "user";
}

/**
 * Converts a rich mention to the API MentionType format.
 * Used when sending data to the API.
 */
export function toMentionType(rich: RichMention): MentionType {
  switch (rich.type) {
    case "agent": {
      return {
        configurationId: rich.id,
      } satisfies AgentMention;
    }
    case "user": {
      return {
        type: "user",
        userId: rich.id,
      } satisfies UserMention;
    }
    default:
      assertNever(rich);
  }
}

export function toRichAgentMentionType(agentConfiguration: {
  sId: string;
  name: string;
  displayName?: string | null;
  pictureUrl: string;
  description: string;
  userFavorite?: boolean;
}): RichAgentMention {
  return {
    id: agentConfiguration.sId,
    type: "agent",
    label: agentDisplayName(agentConfiguration),
    pictureUrl: agentConfiguration.pictureUrl,
    description: agentConfiguration.description,
    userFavorite: agentConfiguration.userFavorite,
  };
}

export function toRichUserMentionType(user: UserType): RichUserMention {
  return {
    id: user.sId,
    type: "user",
    label: user.fullName,
    pictureUrl: user.image ?? "/static/humanavatar/anonymous.png",
    description: user.email,
  };
}
